import { AbilityBuilder, MongoAbility, createMongoAbility } from "@casl/ability";
import { ViewContext } from "src/store/user-store";
import { SubscriptionPlan } from "src/types/interfaces/subscription-plan";
import { securityContextPermissions } from "./security-context-permissions";
import { siteContextPermissions } from "./site-context-permissions";
import { tenantContextPermissions } from "./tenant-context-permissions";

type CRUD = "create" | "read" | "update" | "delete";
type SUBJECTS =
  | "Tenants"
  | "TenantsAdminInvitations"
  | "TenantResources"
  | "TenantBookingRules"
  | "BlackListedLicensePlate"
  | "TermsAndConditions"
  | "PrivacyPolicy"
  | "Statistics"
  | "Password"
  | "IntegrationQuestionnaire"
  | "SiteDetails"
  | "ParkingDetails"
  | "BookingRules"
  | "PermanentBookingRules"
  | "EmployeeStatistics"
  | "EmployeeReservations"
  | "EmployeeInvitations"
  | "EmployeeGroupBookingRules"
  | "EmployeeGroupReservations"
  | "ResourceGroups"
  | "Issues"
  | "GdprClause"
  | "OrganizationDetails"
  | "SpareSpots"
  | "TenantEvChargersPermissionAccess"
  | "SiteChargingRules"
  | "SiteEvChargersElectricityUsage"
  | "TenantEvChargersElectricityUsage"
  | "Notifications"
  | "SystemMessages";

type Abilities = [CRUD, SUBJECTS];

type ParkingActions =
  | CRUD
  | "assign"
  | "detach"
  | "addMap"
  | "deleteMap"
  | "lockSpot"
  | "setAsReserve"
  | "updatePriority"
  | "unlockSpot"
  | "downloadXls";

type ParkingAbilities = [ParkingActions, "ParkingSpots"];

type SitesActions = CRUD | "manageLimits";
type SitesAbilities = [SitesActions, "Sites"];

type ContactActions = CRUD | "send";
type ContactAbilities = [ContactActions, "Contact"];

type NotificationsSettingsActions = "update" | "updateSite";
type NotificationsSettingsAbilities = [NotificationsSettingsActions, "NotificationsSettings"];

interface SiteAdministrators {
  userToken: string;
}

interface SiteSecurityAdministrators {
  userEmail: string;
}

type SiteAdministratorsActions =
  | CRUD
  | "chooseRole"
  | "updatePhoneNumber"
  | "updateRole"
  | "resendInvitation";

type SiteAdministratorsAbilities = [
  SiteAdministratorsActions,
  "SiteAdministrators" | SiteAdministrators,
];

type SiteSecurityAdministratorsActions = CRUD | "resendInvitation";

type SiteSecurityAdministratorsAbilites = [
  SiteSecurityAdministratorsActions,
  "SiteSecurityAdministrators" | SiteSecurityAdministrators,
];

type ReservationActions = CRUD | "finish" | "cancel" | "authorizeAccess" | "changeGroup";
type ReservationAbilities = [ReservationActions, "Reservations"];

type EmployeesActions =
  | CRUD
  | "ban"
  | "changeGroup"
  | "changeLicensePlate"
  | "resendInvitation"
  | "downloadXls";

type EmployeesAbilities = [EmployeesActions, "Employees"];

type EmployeeGroupsActions =
  | CRUD
  | "downloadActiveSessions"
  | "readLatestBadgeStatusHistory"
  | "changeBookingRules"
  | "setAsDefault"
  | "removeAsDefault"
  | "changeName"
  | "changePriority";
type EmployeeGroupsAbilities = [EmployeeGroupsActions, "EmployeeGroups"];

type EmployeeGroupResourceGroupsActions = CRUD | "add" | "remove";
type EmployeeGroupResourceGroupsAbilities = [
  EmployeeGroupResourceGroupsActions,
  "EmployeeGroupResourceGroups",
];

type EmployeeGroupEmployeesActions = CRUD | "add" | "remove";
type EmployeeGroupEmployeesAbilities = [EmployeeGroupEmployeesActions, "EmployeeGroupEmployees"];

type PlansActions =
  | "reserveLongerThan24hOn"
  | "reserveForMoreThan3DaysAheadOn"
  | "chooseRolesOtherThanOwnerOn"
  | "extendReservationByMoreThan24hOn"
  | "downloadStatisticsReportOn"
  | "integrateWithAccessControlOn"
  | "chooseRuleGroupLotteryStrategy"
  | "sharePermanentSpots"
  | "choose15mReservationSlots"
  | "accessPremiumRoutes";
type PlansAbilities = [PlansActions, "subscriptionPlan"];

type ResourceGroupsResourcesActions = CRUD | "assignResource";
type ResourceGroupsResourcesAbilities = [ResourceGroupsResourcesActions, "ResourceGroupsResources"];

interface TenantAdministrators {
  userToken: string;
}
type TenantAdministratorsActions =
  | CRUD
  | "chooseRole"
  | "updatePhoneNumber"
  | "updateRole"
  | "resendInvitation";

type TenantAdministratorsAbilities = [
  TenantAdministratorsActions,
  "TenantAdministrators" | TenantAdministrators,
];

type SiteInvitationsActions = CRUD | "acceptInvitation" | "rejectInvitation";
type SiteInvitationsAbilities = [SiteInvitationsActions, "SiteInvitations"];

type AllSitesBillingActions = CRUD | "changeSpotsLimit" | "requestIntegration";

type AllSitesBillingActionsAbilities = [AllSitesBillingActions, "AllSitesBilling"];

type BillingHistoryActions = CRUD | "downloadInvoice";

type BillingHistoryActionsAbilities = [BillingHistoryActions, "BillingHistory"];

type BillingDetailsActions = CRUD | "downloadInvoice";

type BillingDetailsActionsAbilities = [BillingDetailsActions, "BillingDetails"];

type BillingPlanActions = CRUD | "changeSpotsLimit" | "changeUsersLimit" | "requestIntegration";

type BillingPlanActionsAbilities = [BillingPlanActions, "BillingPlan"];

type BillingActions = CRUD | "changePlan";

type BillingAbilities = [BillingActions, "Billing"];

type SiteRemovalActions = CRUD | "acceptOrReject" | "cancel";
type SiteRemovalAbilities = [SiteRemovalActions, "SiteRemoval"];

type PermanentSpotsActions = CRUD | "cancel" | "share" | "createPermanentBooking";
type PermanentSpotsAbilities = [PermanentSpotsActions, "PermanentSpots"];

type OrganizationRemovalActions = CRUD | "acceptOrReject" | "cancel";
type OrganizationRemovalAbilities = [OrganizationRemovalActions, "OrganizationRemoval"];

type SharedSpotsActions = CRUD | "cancel";
type SharedSpotsAbilities = [SharedSpotsActions, "SharedSpots"];

type SiteChargingSpotsActions =
  | CRUD
  | "manageMap"
  | "manageTenants"
  | "pauseBooking"
  | "unpauseBooking";
type SiteChargingSpotsAbilities = [SiteChargingSpotsActions, "SiteChargingSpots"];

type SiteChargingUsageLimitActions = CRUD | "manageLimit";
type SiteChargingUsageLimitAbilities = [SiteChargingUsageLimitActions, "SiteChargingUsageLimits"];

type TenantEvChargersActions = CRUD | "assign" | "detach";
type TenantEvChargersAbilities = [TenantEvChargersActions, "TenantEvChargers"];

type TenantChargingSpotsActions = "read";
type TenantChargingSpotsAbilities = [TenantChargingSpotsActions, "TenantChargingSpots"];

type EmployeesChargingPermissionsActions = CRUD | "grantPermission" | "revokePermission";
type EmployeesChargingPermissionsAbilities = [
  EmployeesChargingPermissionsActions,
  "EmployeesChargingPermissions",
];

type EmployeesChargingPermissionRequestsActions = CRUD | "approve" | "decline";
type EmployeesChargingPermissionRequestsAbilities = [
  EmployeesChargingPermissionRequestsActions,
  "EmployeesChargingPermissionRequests",
];

type EmployeesChargingBookingsActions = CRUD | "finish";
type EmployeesChargingBookingsAbilities = [
  EmployeesChargingBookingsActions,
  "EmployeesChargingBookings",
];

export type AppAbility = MongoAbility<
  | Abilities
  | ParkingAbilities
  | SitesAbilities
  | SiteAdministratorsAbilities
  | SiteSecurityAdministratorsAbilites
  | ContactAbilities
  | ReservationAbilities
  | EmployeesAbilities
  | EmployeeGroupsAbilities
  | EmployeeGroupResourceGroupsAbilities
  | EmployeeGroupEmployeesAbilities
  | ResourceGroupsResourcesAbilities
  | PlansAbilities
  | TenantAdministratorsAbilities
  | SiteInvitationsAbilities
  | AllSitesBillingActionsAbilities
  | BillingHistoryActionsAbilities
  | BillingDetailsActionsAbilities
  | BillingPlanActionsAbilities
  | BillingHistoryActionsAbilities
  | BillingDetailsActionsAbilities
  | SiteRemovalAbilities
  | OrganizationRemovalAbilities
  | BillingAbilities
  | PermanentSpotsAbilities
  | SharedSpotsAbilities
  | SiteChargingSpotsAbilities
  | SiteChargingUsageLimitAbilities
  | TenantEvChargersAbilities
  | TenantChargingSpotsAbilities
  | EmployeesChargingPermissionsAbilities
  | EmployeesChargingPermissionRequestsAbilities
  | EmployeesChargingBookingsAbilities
  | NotificationsSettingsAbilities
>;

export type Role = "owner" | "admin" | "readonly" | "master";

export function definePermissions(
  viewContext: ViewContext,
  role: Role,
  userEmail: string,
  userToken: string,
  shouldHaveAccessToSiteActionsInTenantContext: boolean,
  isLegacyAgreement: boolean,
  hasAccessControlIntegration: boolean,
  siteHasAccessToEVChargers: boolean,
  tenantHasAccessToEVChargers: boolean,
  isLakesideSite: boolean,
  isSSOUser: boolean,
  organizationPlan?: SubscriptionPlan,
) {
  const builder = new AbilityBuilder<AppAbility>(createMongoAbility);

  switch (viewContext) {
    case "site":
      siteContextPermissions(
        builder,
        role,
        userToken,
        userEmail,
        isLegacyAgreement,
        siteHasAccessToEVChargers,
      );

      builder.can("accessPremiumRoutes", "subscriptionPlan");

      break;

    case "tenant":
      tenantContextPermissions(
        builder,
        role,
        userToken,
        shouldHaveAccessToSiteActionsInTenantContext,
        hasAccessControlIntegration,
        isLegacyAgreement,
        tenantHasAccessToEVChargers,
        isSSOUser,
      );

      builder.can("sharePermanentSpots", "subscriptionPlan");
      builder.can("choose15mReservationSlots", "subscriptionPlan");
      builder.can("accessPremiumRoutes", "subscriptionPlan");

      if (organizationPlan === "premium") {
        builder.can("chooseRolesOtherThanOwnerOn", "subscriptionPlan");
        builder.can("downloadStatisticsReportOn", "subscriptionPlan");
        builder.can("reserveForMoreThan3DaysAheadOn", "subscriptionPlan");
        builder.can("reserveLongerThan24hOn", "subscriptionPlan");
        builder.can("chooseRuleGroupLotteryStrategy", "subscriptionPlan");

        if (role === "owner" || role === "master") {
          builder.can("changeUsersLimit", "BillingPlan");
        }
      }

      // Check premium plan or if Lakeside site
      if (isLakesideSite && organizationPlan === "free") {
        builder.cannot("chooseRuleGroupLotteryStrategy", "subscriptionPlan");
        builder.cannot("sharePermanentSpots", "subscriptionPlan");
        builder.cannot("choose15mReservationSlots", "subscriptionPlan");
        builder.cannot("accessPremiumRoutes", "subscriptionPlan");

        builder.cannot("read", "TenantEvChargersPermissionAccess");
        builder.cannot("update", "TenantEvChargersPermissionAccess");
      }

      break;

    case "security":
      securityContextPermissions(builder);

      break;
    default:
      break;
  }

  return builder.build();
}
